import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { toast } from 'sonner';
import i18n from '../i18n';

/**
 * Saved Pro-listing ids + optimistic toggle (mirrors useSavedJobs). Backend:
 *   GET    /api/pro-listings/saved   -> { listings: [card fields] }
 *   POST   /api/pro-listings/{id}/save
 *   DELETE /api/pro-listings/{id}/save
 *
 * @param {object|null} profile - logged-out (no id) short-circuits the fetch; toggle prompts login.
 */
export const useSavedProListings = (profile) => {
  const [savedListingIds, setSavedListingIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const t = (key, fallback) => i18n.t(key, fallback);

  const fetchSaved = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await api.get('/api/pro-listings/saved', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const listings = res.data?.listings || [];
      setSavedListingIds(listings.map((l) => l.id));
    } catch (error) {
      console.error('[useSavedProListings] fetch failed:', error);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (profile?.id) {
      fetchSaved();
    }
  }, [profile?.id, fetchSaved]);

  const isSaved = useCallback((listingId) => savedListingIds.includes(listingId), [savedListingIds]);

  const toggleSave = useCallback(async (listingId) => {
    if (loading) return; // guard against double-click races on the optimistic toggle
    if (!profile?.id) {
      toast.error(t('services.loginRequired', 'Please log in.'));
      return;
    }
    const wasSaved = savedListingIds.includes(listingId);

    // Optimistic update.
    setSavedListingIds((prev) =>
      wasSaved ? prev.filter((id) => id !== listingId) : [...prev, listingId]
    );
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        // Roll back optimistic update.
        setSavedListingIds((prev) =>
          wasSaved ? [...prev, listingId] : prev.filter((id) => id !== listingId)
        );
        toast.error(t('services.loginRequired', 'Please log in.'));
        return;
      }
      const headers = { Authorization: `Bearer ${session.access_token}` };
      if (wasSaved) {
        await api.delete(`/api/pro-listings/${listingId}/save`, { headers });
        toast.success(t('services.unsaved', 'Removed from saved.'));
      } else {
        await api.post(`/api/pro-listings/${listingId}/save`, {}, { headers });
        toast.success(t('services.saved', 'Saved.'));
      }
    } catch (error) {
      // Roll back on failure.
      setSavedListingIds((prev) =>
        wasSaved ? [...prev, listingId] : prev.filter((id) => id !== listingId)
      );
      const detail = error?.response?.data?.detail;
      toast.error(detail || t('services.saveError', 'Could not update saved listings.'));
    } finally {
      setLoading(false);
    }
  }, [profile?.id, savedListingIds, loading]);

  return { savedListingIds, isSaved, toggleSave, loading, refetch: fetchSaved };
};

export default useSavedProListings;
